/* SHARED UPLOADER v1.1.7 #0020fe405f59. Do not edit this copy: edit freshmagnets.com/uploader/src and run "npm run sync". */
// Fresh Magnets photo uploader: modal flow, reactive slot grid, per-photo
// pan/zoom crop, and a "use one photo for all" mode.
// Pure helpers are exported for unit testing.

// Never more than four columns: the uploader sits in the product info column,
// so five across leaves ~86px cells where the controls no longer fit. Precise
// cropping and text happen in the editor (see _openEditor), not on the thumbs.
export function columnsForCount(n) {
  if (n <= 1) return 1;
  if (n <= 4) return 2;
  if (n <= 9) return 3;
  return 4;
}

export function isComplete(slots) {
  return slots.length > 0 && slots.every((s) => s && s.filled);
}

// Headers for one photo upload. The pack and "one photo for all" ride along so
// the uploads dashboard can tell which pack a session was for, even when the
// customer never reaches checkout.
export function uploadHeaders(ref, pack, sameForAll, visitor) {
  const h = { 'Content-Type': 'image/jpeg', 'X-Order-Ref': ref };
  const n = parseInt(pack, 10);
  if (n > 0) h['X-Pack'] = String(n);
  h['X-Same'] = sameForAll ? '1' : '0';
  if (visitor && /^[A-Za-z0-9-]{8,64}$/.test(visitor)) h['X-Visitor'] = visitor;
  return h;
}

// Decode a chosen photo for drawing. createImageBitmap is fastest, but older
// iPads/iPhones lack it or reject the options, so fall back to a plain <img>
// (browsers apply the photo's EXIF orientation to images on their own).
export async function decodeImage(file, deps) {
  const d = deps || {};
  const cib = 'cib' in d ? d.cib : (typeof createImageBitmap === 'function' ? createImageBitmap : null);
  const loadImg = d.loadImg || loadImageElement;
  if (cib) {
    try { return await cib(file, { imageOrientation: 'from-image' }); } catch (e) { /* try the next way */ }
  }
  return loadImg(file);
}
function loadImageElement(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => resolve({ image: img, width: img.naturalWidth, height: img.naturalHeight, close: () => URL.revokeObjectURL(url) });
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('image decode failed')); };
    img.src = url;
  });
}

// canvas.toBlob can hand back null on low-memory devices; build the JPEG from
// a data URL instead so the upload still has a file.
export function dataUrlToBlob(dataUrl) {
  const parts = String(dataUrl).split(',');
  const mime = (/^data:([^;]+)/.exec(parts[0]) || [])[1] || 'image/jpeg';
  const bin = atob(parts[1] || '');
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}

// Retry a request that failed on the network or with a temporary server error
// (429/5xx). A 4xx answer is final: sending the same file again cannot help.
export async function withRetry(fn, tries, waitMs) {
  const n = tries || 3;
  for (let i = 1; ; i++) {
    try {
      return await fn();
    } catch (e) {
      const temporary = !e || !e.status || e.status === 429 || e.status >= 500;
      if (i >= n || !temporary) throw e;
      await new Promise((r) => setTimeout(r, (waitMs == null ? 800 : waitMs) * i));
    }
  }
}

// A random id kept in this browser so the shop's uploads dashboard can tell a
// returning visitor from a new one. Nothing personal; private windows or
// blocked storage simply get no id.
const VISITOR_KEY = 'fm_vid';
export function visitorId(storage) {
  try {
    const st = storage || window.localStorage;
    let v = st.getItem(VISITOR_KEY);
    if (!v || !/^[A-Za-z0-9-]{8,64}$/.test(v)) {
      v = window.crypto && crypto.randomUUID ? crypto.randomUUID() : 'v' + Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
      st.setItem(VISITOR_KEY, v);
    }
    return v;
  } catch (e) {
    return '';
  }
}

// "Use one photo for all" after a pack change: the new boxes hold the same
// photo with the same crop, so when that photo is already uploaded they can
// share its link instead of waiting for another upload (which hid the
// thumbnails and locked the add button until the window was opened again).
export function shareUploadedCopy(slots, src) {
  if (!src || !src.uploadedUrl || src.dirty) return;
  slots.forEach((s) => {
    if (s === src || !s.filled || s.file !== src.file) return;
    s.uploadedUrl = src.uploadedUrl;
    s.thumb = src.thumb;
    s.dirty = false;
  });
}

export function filledCount(slots) {
  return slots.filter((s) => s && s.filled).length;
}

export function shapeClass(shape) {
  const s = (shape || 'square').toLowerCase();
  if (s.indexOf('round') !== -1 || s.indexOf('circle') !== -1) return 'round';
  if (s.indexOf('rect') !== -1) return 'rect';
  return 'square';
}

// Print canvas size in px at 300 DPI, parsed from a "WxH" product metafield.
// Falls back to the per-shape defaults when the metafield is missing or malformed.
export function printSize(value, shape) {
  const parts = String(value || '')
    .toLowerCase()
    .split('x');
  const w = parseInt(parts[0], 10);
  const h = parseInt(parts[1], 10);
  if (w > 0 && h > 0) return [w, h];
  const cls = shapeClass(shape);
  if (cls === 'rect') return [600, 900];
  if (cls === 'round') return [675, 675];
  return [750, 750];
}

// Human-readable magnet size for the order line, derived from the print size.
// The print file is rendered at 300 DPI, so 600x900 px is a 2 x 3 inch magnet.
// This is written as a VISIBLE line item property, because neither the product
// title nor the variant name ("6-Pack") says what size was actually bought, and
// fulfillment needs to read it off the order at a glance.
export function sizeLabel(value, shape) {
  const [w, h] = printSize(value, shape);
  const inch = (px) => {
    const v = px / 300;
    return String(Math.round(v * 100) / 100);
  };
  const cls = shapeClass(shape);
  if (cls === 'round') return inch(w) + '" round';
  return inch(w) + '" x ' + inch(h) + (w === h ? '" square' : '"');
}

// Orientation a photo naturally wants. Only the 2 x 3 rectangle offers a
// choice; a clearly wider-than-tall photo starts as landscape, anything else
// (including near-square) as portrait. The customer can flip it per slot.
export function layoutForPhoto(width, height) {
  return width > height * 1.05 ? 'landscape' : 'portrait';
}

// Print canvas for one slot: the product print size, swapped for a landscape
// rectangle so the on-screen 3:2 crop is reproduced at 900x600.
export function slotPrintSize(base, layout, shape) {
  if (shapeClass(shape) === 'rect' && layout === 'landscape') return [base[1], base[0]];
  return base;
}

// Hidden line-item property listing which magnets are landscape, as "|1|4|"
// so Liquid can test `contains '|4|'` without "1" matching "11".
// Index N of a hidden "properties[_photo_N_url]" field, or null for any other name.
export function photoPropIndex(name) {
  const m = /^properties\[_photo_(\d+)_url\]$/.exec(name || '');
  return m ? Number(m[1]) : null;
}

// Shopify lists line-item properties in form order. Fields for photos 7+ are
// created when the customer moves up from the 6-pack, so without this the
// order page shows photos 1-6, then shape and size, then the rest. Moves the
// photo fields to the end in number order, then the trailing tags after them.
export const TRAILING_PROPS = ['_shape', '_landscape', 'Size', 'Captions'];
export function orderPropInputs(form) {
  const photos = [...form.querySelectorAll('input[name^="properties[_photo_"]')]
    .filter((el) => photoPropIndex(el.name) !== null)
    .sort((a, b) => photoPropIndex(a.name) - photoPropIndex(b.name));
  photos.forEach((el) => form.appendChild(el));
  TRAILING_PROPS.forEach((name) => {
    const el = form.querySelector('input[name="properties[' + name + ']"]');
    if (el) form.appendChild(el);
  });
}

// Readable on the order page: "10, 11, 12, 14", or "none" when every 2 x 3 is
// upright. Squares and rounds have no orientation, so the field is left out.
// (Orders before v1.1.4 used "|10|11|"; readers accept both.)
export function landscapeProp(slots, shape) {
  if (shapeClass(shape) !== 'rect') return '';
  const idx = slots
    .map((s, i) => (s && s.layout === 'landscape' ? i + 1 : 0))
    .filter((n) => n > 0);
  return idx.length ? idx.join(', ') : 'none';
}

// ---------- Captions ----------
// Optional text on a magnet ("I love you", "Best Dad"). Previewed on the slot
// and burned into the 300 DPI print file at composite time, so nothing
// downstream (worker, print sheet, admin proofs) has to know about it.
export const CAPTION_FONTS = [
  { id: 'script', label: 'Script', family: '"Great Vibes", cursive', weight: 400, gf: 'Great+Vibes' },
  { id: 'satisfy', label: 'Satisfy', family: '"Satisfy", cursive', weight: 400, gf: 'Satisfy' },
  { id: 'hand', label: 'Handwritten', family: '"Caveat", cursive', weight: 600, gf: 'Caveat:wght@600' },
  { id: 'serif', label: 'Serif', family: '"Playfair Display", serif', weight: 500, gf: 'Playfair+Display:wght@500' },
  { id: 'elegant', label: 'Elegant', family: '"Dancing Script", cursive', weight: 600, gf: 'Dancing+Script:wght@600' },
  { id: 'modern', label: 'Modern', family: '"Montserrat", sans-serif', weight: 600, gf: 'Montserrat:wght@600' },
  { id: 'clean', label: 'Clean', family: '"Jost", sans-serif', weight: 500, gf: '' },
  { id: 'bold', label: 'Bold', family: '"Bebas Neue", sans-serif', weight: 400, gf: 'Bebas+Neue' },
  { id: 'oswald', label: 'Oswald', family: '"Oswald", sans-serif', weight: 500, gf: 'Oswald:wght@500' },
];
// Look of the letters when there is no backdrop. Shadow is a soft dark glow
// that keeps white text readable on any photo; outline is a thin dark edge.
export const CAPTION_EFFECTS = [
  { id: 'shadow', label: 'Shadow' },
  { id: 'outline', label: 'Outline' },
  { id: 'none', label: 'None' },
];
// Optional second line (a date, a name): its starting font size relative to line one.
export const CAPTION_LINE2_RATIO = 0.5;
export const CAPTION_LINE2_MAX = 30;
// Line two never ends up bigger than this share of line one's fitted size.
export const CAPTION_LINE2_CAP = 0.62;

export const CAPTION_PHRASES = [
  'I love you', 'Happy Birthday', 'Happy Anniversary', 'Best Mom', 'Best Dad',
  'Best Grandma', 'Best Grandpa', 'Class of 2026', "We're Engaged", 'Welcome Baby',
  'In Loving Memory', 'Our Adventure', 'Family', 'Forever',
];
export const CAPTION_COLORS = [
  { id: 'white', hex: '#FFFFFF' },
  { id: 'black', hex: '#1E2A22' },
  { id: 'emerald', hex: '#28433A' },
  { id: 'terracotta', hex: '#C2410C' },
  { id: 'gold', hex: '#C9A24D' },
];
export const CAPTION_SIZES = { sm: 0.09, md: 0.12, lg: 0.16 };
// Backdrop thickness: vertical padding around the text line, as a fraction
// of the font size. Independent of the text size slider.
export const CAPTION_BAND_PAD = { sm: 0.06, md: 0.18, lg: 0.34 };
export const CAPTION_SIZE_MIN = 7;
export const CAPTION_SIZE_MAX = 20;

// Font size as a fraction of the magnet height. Accepts the old sm/md/lg
// names or a percent number from the slider (7 to 20).
export function captionSizeFrac(size) {
  if (typeof size === 'number' && size > 0) return Math.min(CAPTION_SIZE_MAX, Math.max(CAPTION_SIZE_MIN, size)) / 100;
  return CAPTION_SIZES[size] || CAPTION_SIZES.md;
}

export function defaultCaption(text) {
  return { text: text || '', text2: '', font: 'script', font2: 'oswald', color: '#FFFFFF', band: 'dark', bandOpacity: 0.45, bandSize: 'md', size: 12, pos: 'bottom', effect: 'shadow' };
}

export function captionFont(id) {
  return CAPTION_FONTS.find((f) => f.id === id) || CAPTION_FONTS[0];
}

export function captionFontCss(px, fontId) {
  const f = captionFont(fontId);
  return f.weight + ' ' + px + 'px ' + f.family;
}

// Geometry of the caption band in px for a W x H magnet. Everything is a
// fraction of the HEIGHT so the on-screen preview and the print file agree.
// Top / bottom bands run flush to the edge (they bleed off the magnet) with
// the text centred in the band. A 2% strip (about 1.2 mm on a 2" magnet,
// more than a die cut drifts) must stay clear of glyphs: for thin bands the
// band grows just enough that the centred text (ink is at most ~0.5 em
// either side of centre) still clears it.
// `textY` = text centre.
// On a ROUND magnet the usable width at the text line is the circle's chord
// there, not the full width, so the text is fitted to that chord.
export function captionMetrics(cap, W, H, shape) {
  const frac = captionSizeFrac(cap.size);
  const fontPx = H * frac;
  const padY = fontPx * (CAPTION_BAND_PAD[cap.bandSize] || CAPTION_BAND_PAD.md);
  const lineH = fontPx * 1.1 + padY * 2;
  const inset = H * 0.02;
  const glyphHalf = fontPx * 0.5;
  const edgeBandH = Math.max(lineH + inset, 2 * (inset + glyphHalf));
  let bandTop;
  let bandH;
  let textY;
  if (cap.pos === 'top') {
    bandTop = 0;
    bandH = edgeBandH;
  } else if (cap.pos === 'middle') {
    bandH = lineH;
    bandTop = (H - bandH) / 2;
  } else {
    bandH = edgeBandH;
    bandTop = H - bandH;
  }
  textY = bandTop + bandH / 2;
  let maxTextW = W * 0.86;
  if (shape === 'round') {
    const R = Math.min(W, H) / 2;
    const dy = Math.abs(textY - H / 2);
    // chord at the text line, then keep the glyph box inside it: the line box
    // is ~0.6 fontPx tall, so measure the chord at its top/bottom edge.
    const dyEdge = dy + fontPx * 0.35;
    const chord = 2 * Math.sqrt(Math.max(0, R * R - dyEdge * dyEdge));
    maxTextW = Math.max(W * 0.2, chord * 0.9);
  }
  return { fontPx, bandH, bandTop, textY, maxTextW };
}

// Shrink a font size until the measured text fits. `measure(px)` returns the
// text width at that size.
export function fitFont(measure, fontPx, maxW) {
  let px = fontPx;
  for (let i = 0; i < 10; i++) {
    const w = measure(px);
    if (w <= maxW || px <= 6) break;
    px = Math.max(6, px * (maxW / w) * 0.98);
  }
  return px;
}

// Full layout for a caption, from MEASURED ink, used by both the preview and
// the print file. `measure(text, fontId, px)` returns { w, asc, desc }: the
// advance width and the ink ascent / descent from the alphabetic baseline
// (canvas measureText actualBoundingBox*). Each line's ink is centred on its y.
// One line keeps the original geometry (captionMetrics). Two lines: the ink of
// line one, a gap of CAPTION_INK_GAP x line one's size, then the ink of line
// two; the band (backdrop) wraps that block plus its padding, and for top and
// bottom bands a 2% strip next to the magnet edge stays clear of ink. Rounds
// fit each line to the circle's chord at its own height and the block starts a
// little lower at the top, where the circle is narrow.
export const CAPTION_INK_GAP = 0.2;
export function captionLayout(cap, W, H, shape, measure) {
  const t1 = (cap.text || '').trim();
  const t2 = (cap.text2 || '').trim();
  const f2id = cap.font2 || 'oswald';
  if (!t1 && !t2) return null;
  if (!t1 || !t2) {
    const text = t1 || t2;
    const fontId = t1 ? cap.font : f2id;
    const m = captionMetrics({ ...cap, text2: '' }, W, H, shape);
    const px = fitFont((p) => measure(text, fontId, p).w, m.fontPx, m.maxTextW);
    return { bandTop: m.bandTop, bandH: m.bandH, lines: [{ text, font: fontId, px, y: m.textY }] };
  }
  const f = H * captionSizeFrac(cap.size);
  const pad = f * (CAPTION_BAND_PAD[cap.bandSize] || CAPTION_BAND_PAD.md);
  const inset = H * 0.02;
  const R = Math.min(W, H) / 2;
  const widthAt = (yTop, yBot) => {
    if (shape !== 'round') return W * 0.86;
    // the narrower of the chords at the ink's top and bottom edge
    const dy = Math.max(Math.abs(yTop - H / 2), Math.abs(yBot - H / 2));
    const chord = 2 * Math.sqrt(Math.max(0, R * R - dy * dy));
    return Math.max(W * 0.2, chord * 0.9);
  };
  const roundTopDrop = shape === 'round' && cap.pos === 'top' ? H * 0.07 : 0;
  let px1 = f;
  let px2 = f * CAPTION_LINE2_RATIO;
  let out = null;
  for (let pass = 0; pass < 3; pass++) {
    const m1 = measure(t1, cap.font, px1);
    const m2 = measure(t2, f2id, px2);
    const half1 = (m1.asc + m1.desc) / 2;
    const half2 = (m2.asc + m2.desc) / 2;
    const gap = px1 * CAPTION_INK_GAP;
    const blockH = half1 * 2 + gap + half2 * 2;
    let bandTop;
    let bandH;
    let blockTop;
    if (cap.pos === 'top') {
      bandTop = 0;
      bandH = blockH + pad * 2 + inset + roundTopDrop;
      blockTop = inset + pad + roundTopDrop;
    } else if (cap.pos === 'middle') {
      bandH = blockH + pad * 2;
      bandTop = (H - bandH) / 2;
      blockTop = bandTop + pad;
    } else {
      bandH = blockH + pad * 2 + inset;
      bandTop = H - bandH;
      blockTop = bandTop + pad;
    }
    const y1 = blockTop + half1;
    const y2 = blockTop + half1 * 2 + gap + half2;
    out = { bandTop, bandH, lines: [{ text: t1, font: cap.font, px: px1, y: y1 }, { text: t2, font: f2id, px: px2, y: y2 }] };
    // refit to the room each line has at its position, then place again
    const n1 = fitFont((p) => measure(t1, cap.font, p).w, f, widthAt(y1 - half1, y1 + half1));
    let n2 = fitFont((p) => measure(t2, f2id, p).w, f * CAPTION_LINE2_RATIO, widthAt(y2 - half2, y2 + half2));
    n2 = Math.min(n2, n1 * CAPTION_LINE2_CAP);
    if (Math.abs(n1 - px1) < 0.5 && Math.abs(n2 - px2) < 0.5) break;
    px1 = n1;
    px2 = n2;
  }
  // Final guarantee: each line fits the room at its final position. Only ever
  // shrinks, so the ink stays inside the band and clear of the edge.
  out.lines.forEach((ln) => {
    const mm = measure(ln.text, ln.font, ln.px);
    const half = (mm.asc + mm.desc) / 2;
    ln.px = fitFont((p) => measure(ln.text, ln.font, p).w, ln.px, widthAt(ln.y - half, ln.y + half));
  });
  out.lines[1].px = Math.min(out.lines[1].px, out.lines[0].px * CAPTION_LINE2_CAP);
  return out;
}

export function hexToRgba(hex, alpha) {
  const h = String(hex || '#000000').replace('#', '');
  const n = parseInt(h.length === 3 ? h.split('').map((c) => c + c).join('') : h, 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  return 'rgba(' + r + ',' + g + ',' + b + ',' + alpha + ')';
}

export function captionBandColor(cap) {
  if (cap.band === 'white') return hexToRgba('#FFFFFF', cap.bandOpacity);
  if (cap.band === 'dark') return hexToRgba('#141C18', cap.bandOpacity);
  return null;
}

// Visible order property. Identical texts are grouped so the cart line stays
// short: "Happy Birthday (all 6)", or "I love you (1, 2) · Best Dad (4)".
// Empty when no magnet has text.
export function captionSummary(slots) {
  const groups = [];
  slots.forEach((s, i) => {
    const c = s && s.caption;
    const t1 = c && c.text ? c.text.trim() : '';
    const t2 = c && c.text2 ? c.text2.trim() : '';
    const t = t1 && t2 ? t1 + ' / ' + t2 : t1 || t2;
    if (!t) return;
    let g = groups.find((x) => x.text === t);
    if (!g) {
      g = { text: t, idx: [] };
      groups.push(g);
    }
    g.idx.push(i + 1);
  });
  if (!groups.length) return '';
  const total = slots.filter(Boolean).length;
  // Reads on the cart, order and packing slip as "Magnet 1: Dog · Magnet 4: Happy Birthday".
  if (groups.length === 1 && groups[0].idx.length === total && total > 1) {
    return 'All ' + total + ' magnets: ' + groups[0].text;
  }
  return groups
    .map((g) => (g.idx.length === 1 ? 'Magnet ' : 'Magnets ') + g.idx.join(', ') + ': ' + g.text)
    .join(' · ');
}

// How far the visible ink of `text` sits above the em-box middle, in px, for
// the given font string. Centring on the em box leaves capitals looking high
// (no descenders), so both the print and the preview shift by this amount.
// Returns 0 when the browser lacks the measurement APIs.
export function captionInkShift(ctx, text, fontCss) {
  ctx.font = fontCss;
  ctx.textBaseline = 'alphabetic';
  const mt = ctx.measureText(text);
  if (mt.actualBoundingBoxAscent === undefined || mt.fontBoundingBoxAscent === undefined) return 0;
  const inkMid = (mt.actualBoundingBoxAscent - mt.actualBoundingBoxDescent) / 2;
  const emMid = (mt.fontBoundingBoxAscent - mt.fontBoundingBoxDescent) / 2;
  return inkMid - emMid;
}

// Effect actually used: only without a backdrop (a band already gives contrast).
export function captionEffect(cap) {
  if (captionBandColor(cap)) return 'none';
  return CAPTION_EFFECTS.some((e) => e.id === cap.effect) ? cap.effect : 'shadow';
}

// True for white and other light text colours (relative luminance above 0.35, so gold counts as light).
export function isLightColor(hex) {
  const h = String(hex || '#FFFFFF').replace('#', '');
  const n = parseInt(h.length === 3 ? h.split('').map((c) => c + c).join('') : h, 16);
  if (Number.isNaN(n)) return true;
  const lin = (v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  };
  const L = 0.2126 * lin((n >> 16) & 255) + 0.7152 * lin((n >> 8) & 255) + 0.0722 * lin(n & 255);
  return L > 0.35;
}

// Colours for the effect: light text gets a dark shadow / outline, dark text
// gets a light glow / outline, so the letters always stand off the photo.
export function captionEffectColors(textColor) {
  if (isLightColor(textColor)) {
    return { soft: 'rgba(0,0,0,.6)', tight: 'rgba(0,0,0,.4)', line: 'rgba(20,15,15,.85)', offset: true };
  }
  return { soft: 'rgba(255,255,255,.75)', tight: 'rgba(255,255,255,.55)', line: 'rgba(255,255,255,.9)', offset: false };
}

// CSS text-shadow for the on-screen preview, matching drawCaptionLine.
export function captionShadowCss(px, effect, textColor) {
  const r = (v) => Math.max(0.5, Math.round(v * 10) / 10);
  const col = captionEffectColors(textColor);
  if (effect === 'shadow') {
    return '0 ' + (col.offset ? r(px * 0.06) : 0) + 'px ' + r(px * 0.22) + 'px ' + col.soft + ', 0 0 ' + r(px * 0.07) + 'px ' + col.tight;
  }
  if (effect === 'outline') {
    const w = r(px * 0.045);
    return [[w, 0], [-w, 0], [0, w], [0, -w], [w, w], [-w, -w], [w, -w], [-w, w]].map((o) => o[0] + 'px ' + o[1] + 'px 0 ' + col.line).join(', ');
  }
  return 'none';
}

// One centred line, ink centred on y, with the chosen effect.
function drawCaptionLine(ctx, W, text, fontId, px, y, color, effect) {
  ctx.font = captionFontCss(px, fontId);
  ctx.textAlign = 'center';
  // Centre the visible INK on y: alphabetic baseline placed so the ink
  // box (measured) straddles y. Exact for any font or word.
  ctx.textBaseline = 'alphabetic';
  const mt = ctx.measureText(text);
  const asc = mt.actualBoundingBoxAscent || px * 0.7;
  const desc = mt.actualBoundingBoxDescent || 0;
  const baselineY = y + (asc - desc) / 2;
  ctx.fillStyle = color;
  const col = captionEffectColors(color);
  if (effect === 'shadow') {
    // Wide soft glow, then a tight one: dark behind light text, light behind dark text.
    ctx.shadowColor = col.soft;
    ctx.shadowBlur = px * 0.22;
    ctx.shadowOffsetY = col.offset ? px * 0.06 : 0;
    ctx.fillText(text, W / 2, baselineY);
    ctx.shadowColor = col.tight;
    ctx.shadowBlur = px * 0.07;
    ctx.shadowOffsetY = 0;
    ctx.fillText(text, W / 2, baselineY);
  } else if (effect === 'outline') {
    ctx.lineJoin = 'round';
    ctx.lineWidth = px * 0.09;
    ctx.strokeStyle = col.line;
    ctx.strokeText(text, W / 2, baselineY);
    ctx.fillText(text, W / 2, baselineY);
  } else {
    ctx.fillText(text, W / 2, baselineY);
  }
  ctx.shadowColor = 'transparent';
  ctx.shadowBlur = 0;
  ctx.shadowOffsetY = 0;
}

// Draw the caption onto a 2D context of a W x H canvas.
export function drawCaption(ctx, W, H, cap, shape) {
  const L = captionLayout(cap, W, H, shape, (t, fontId, px) => {
    ctx.font = captionFontCss(px, fontId);
    const mt = ctx.measureText(t);
    return { w: mt.width, asc: mt.actualBoundingBoxAscent || px * 0.7, desc: mt.actualBoundingBoxDescent || 0 };
  });
  if (!L) return;
  const band = captionBandColor(cap);
  if (band) {
    ctx.fillStyle = band;
    ctx.fillRect(0, L.bandTop, W, L.bandH);
  }
  const effect = captionEffect(cap);
  const color = cap.color || '#FFFFFF';
  L.lines.forEach((ln) => drawCaptionLine(ctx, W, ln.text, ln.font, ln.px, ln.y, color, effect));
}


// Clamp a pan offset so the image still covers its frame. coverSize is the
// image's on-screen length along this axis at zoom 1 (object-fit: cover), so a
// tall photo can be slid up and down even before zooming.
export function clampOffset(value, scale, size, coverSize) {
  const c = coverSize || size;
  const max = Math.max(0, (scale * c - size) / 2);
  return Math.max(-max, Math.min(max, value));
}

// On-screen [width, height] of a photo filling a frame (object-fit: cover), or
// null while the photo or the frame has no size yet.
export function coverSize(iw, ih, cw, ch) {
  if (!iw || !ih || !cw || !ch) return null;
  const k = Math.max(cw / iw, ch / ih);
  return [iw * k, ih * k];
}

// Where a new photo starts: a photo taller than its frame starts near the top
// (faces and heads are rarely in the middle of a tall photo), not dead center.
const TOP_BIAS = 0.7;
export function startOffsetY(iw, ih, cw, ch) {
  const c = coverSize(iw, ih, cw, ch);
  if (!c || c[1] <= ch + 1) return 0;
  return ((c[1] - ch) / 2) * TOP_BIAS;
}

// R2 key of an uploaded print file, from its public URL.
export function uploadKey(url) {
  try { return decodeURIComponent(new URL(url).pathname.replace(/^\//, '')); } catch (e) { return ''; }
}

// The customer's original photo, kept so the shop can re-crop it later. The
// long side is capped at ORIGINAL_MAX px: plenty to re-crop a 3" magnet, and
// a fraction of a phone camera file.
export const ORIGINAL_MAX = 2400;
export async function originalBlob(file, deps) {
  const bitmap = await decodeImage(file, deps);
  const k = Math.min(1, ORIGINAL_MAX / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(bitmap.width * k));
  canvas.height = Math.max(1, Math.round(bitmap.height * k));
  const ctx = canvas.getContext('2d');
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(bitmap.image || bitmap, 0, 0, canvas.width, canvas.height);
  if (bitmap.close) bitmap.close();
  const blob = await new Promise((resolve) => {
    try { canvas.toBlob(resolve, 'image/jpeg', 0.9); } catch (e) { resolve(null); }
  });
  return blob || dataUrlToBlob(canvas.toDataURL('image/jpeg', 0.9));
}

/*
 * Customer-facing texts. Each store theme can override any of them with
 *   <script type="application/json" data-fm-texts>{"title": "..."}</script>
 * inside <photo-uploader>. Defaults are the Fresh Magnets wording. {n} and
 * {total} are filled in where a text carries a count. legend* entries are
 * HTML (they use <b> for the button glyphs).
 */
export const UPLOADER_TEXTS = {
  openLabel: 'Upload pictures',
  helpText: 'Add your photos, then add the set to your cart.',
  editPictures: 'Edit pictures',
  continueUploading: 'Continue uploading',
  title: 'Upload your photos',
  close: 'Close',
  sub: 'One photo per magnet. The grid matches the pack you picked.',
  legendLabel: 'How to edit a photo',
  legendAdd: '<b>+</b> on an empty box adds a photo.',
  legendZoomFine: '<b>+</b> / <b>&minus;</b> on a photo zoom in and out. Once zoomed, drag the photo to choose what shows.',
  legendZoomTouch: '<b>+</b> / <b>&minus;</b> on a photo zoom in and out. Tap the photo to drag, pinch and crop it exactly.',
  legendSwap: '<b>&#8635;</b> swaps that photo for a different one.',
  legendEdit: '<b>&#9998;</b> or a tap on the photo opens it big: crop precisely and add text, like a name or "Happy Birthday".',
  legendRect: '<b>&#9647;</b> / <b>&#9645;</b> in the corner sets that magnet to portrait or landscape. A landscape box is wider, not smaller: same magnet, turned on its side.',
  countAdded: '{n} of {total} photos added',
  oneForAll: 'Using 1 photo for all {total} magnets',
  bulkHint: 'Bulk pack: "Use one photo for all magnets" is on, so you upload once. Turn it off if you want a different photo on each magnet.',
  sameLabel: 'Use one photo for all magnets',
  footNote: 'Tip: tap any photo to open it big, crop it precisely and add text.',
  done: 'Done',
  uploading: 'Uploading...',
  preparing: 'Preparing your photos...',
  uploadingN: 'Uploading photo {n} of {total}...',
  uploadFailed: 'Upload failed. Check your connection and try again.',
  prepareFailed: "We couldn't prepare your photos on this device. Please try another browser, or contact us and we'll help.",
  editDialog: 'Edit photo',
  editTitle: 'Magnet {n} of {total}',
  closeEditor: 'Close editor',
  zoomIn: 'Zoom in',
  zoomOut: 'Zoom out',
  changePhoto: 'Change photo',
  editPhoto: 'Edit photo and text',
  replacePhoto: 'Replace photo',
  remove: 'Remove',
  stageHint: 'Drag the photo to move it. Pinch, scroll, or use &minus; and + to zoom. What you see here is what we press.',
  layoutLabel: 'Magnet layout',
  portrait: 'Portrait',
  landscape: 'Landscape',
  capTitle: 'Text on this magnet',
  capPlaceholder: 'Type your text',
  capHelp: 'Type your own short text, or tap one of the phrases below. Up to 40 characters.',
  font: 'Font',
  line2Placeholder: 'Second line, optional (a date or a name)',
  line2Font: 'Line 2 font',
  color: 'Color',
  customColor: 'Custom color',
  backdrop: 'Backdrop',
  backdropOpacity: 'Backdrop opacity',
  backdropHeight: 'Backdrop height',
  effect: 'Text effect',
  effectHint: 'For text with no backdrop. Shadow keeps white text easy to read on any photo.',
  size: 'Size',
  textSize: 'Text size',
  sizeHint: 'Long text shrinks to fit. Round magnets have less room near the edge.',
  position: 'Position',
  applyAll: 'Apply to all magnets',
  appliedAll: 'Applied to all',
  removeText: 'Remove text',
  prev: '&lsaquo; Previous',
  next: 'Next &rsaquo;',
  thumbsOneForAll: '1 photo, used for all {total} magnets',
  thumbsOne: '{n} photo added',
  thumbsMany: '{n} photos added',
  thumbAlt: 'Uploaded photo {n}',
  photoAlt: 'Your photo for magnet {n}',
  // Add-to-cart label while photos are still missing. Empty = leave the
  // theme's own label alone (the button is disabled either way).
  addLocked: '',
  more: '+{n} more',
};

// Fill {n} / {total} in a text.
export function fmt(text, vars) {
  return String(text).replace(/\{(\w+)\}/g, (m, k) => (vars && k in vars ? String(vars[k]) : m));
}

const esc = (s) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
// Texts that may carry an entity (&minus;, &lsaquo;) but no tags: keep the
// entity, escape anything else.
const ent = (s) => esc(s).replace(/&amp;(#\d+|[a-z]+);/gi, '&$1;');
const ICON_CLOSE = '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>';

/*
 * The upload bar shown on the product page. A theme normally renders this
 * itself (so the button is there before the script loads, with no layout
 * shift); the element only builds it when the theme left it out.
 */
export function uploaderBar(t) {
  return '<div class="fm-up__bar">'
    + '<button type="button" class="fm-up__open" data-fm-open>'
    + '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M12 16V4M7 9l5-5 5 5"/><path d="M5 16v2a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-2"/></svg>\n'
    + '<span data-fm-openlabel>' + esc(t.openLabel) + '</span></button>\n'
    + '<span class="fm-up__status" data-fm-status>' + esc(t.helpText) + '</span></div>\n'
    + '<div class="fm-up__thumbs" data-fm-thumbs hidden></div>';
}

/*
 * The upload window and the one-magnet editor. Built by the element, so a
 * theme only needs <photo-uploader> with its data and, optionally, the bar.
 * rectTip adds the portrait/landscape line for 2 x 3 products.
 */
export function uploaderModal(t, rectTip) {
  const capRow = (label, inner) => '<div class="fm-up__cap-row"><span class="fm-up__cap-lbl">' + esc(label) + '</span>' + inner + '</div>';
  return '<div class="fm-up__modal" data-fm-modal hidden>'
    + '<div class="fm-up__overlay" data-fm-close></div>'
    + '<div class="fm-up__panel" role="dialog" aria-modal="true" aria-label="' + esc(t.title) + '">'
    + '<div class="fm-up__phead"><h3 class="fm-up__ptitle">' + esc(t.title) + '</h3>'
    + '<button type="button" class="fm-up__x" data-fm-close aria-label="' + esc(t.close) + '">' + ICON_CLOSE + '</button></div>'
    + '<p class="fm-up__psub">' + esc(t.sub) + '</p>'
    + '<ul class="fm-up__legend" aria-label="' + esc(t.legendLabel) + '">'
    + '<li>' + t.legendAdd + '</li>'
    + '<li class="fm-up__tip-fine">' + t.legendZoomFine + '</li>'
    + '<li class="fm-up__tip-touch">' + t.legendZoomTouch + '</li>'
    + '<li>' + t.legendSwap + '</li>'
    + '<li>' + t.legendEdit + '</li>'
    + (rectTip ? '<li>' + t.legendRect + '</li>' : '')
    + '</ul>'
    + '<p class="fm-up__pmeta" data-fm-status2>' + esc(fmt(t.countAdded, { n: 0, total: 0 })) + '</p>'
    + '<p class="fm-up__bulkhint" data-fm-bulkhint hidden>' + esc(t.bulkHint) + '</p>'
    + '<label class="fm-up__same"><input type="checkbox" data-fm-same>'
    + '<span class="fm-up__sameui" aria-hidden="true"></span>'
    + '<span class="fm-up__samelbl">' + esc(t.sameLabel) + '</span></label>'
    + '<div class="fm-up__grid" data-fm-grid></div>'
    + '<div class="fm-up__pfoot"><span class="fm-up__pnote">' + esc(t.footNote) + '</span>'
    + '<span class="fm-up__upmsg" data-fm-upmsg hidden></span>'
    + '<button type="button" class="fm-up__done" data-fm-done disabled>' + esc(t.done) + '</button></div>'
    + '</div>'
    + '<div class="fm-up__edit" data-fm-edit hidden>'
    + '<div class="fm-up__editbox" role="dialog" aria-modal="true" aria-label="' + esc(t.editDialog) + '">'
    + '<div class="fm-up__edithead"><span class="fm-up__edittitle">'
    + fmt(esc(t.editTitle), { n: '<span data-fm-edit-n>1</span>', total: '<span data-fm-edit-total>6</span>' })
    + '</span><button type="button" class="fm-up__x" data-fm-edit-close aria-label="' + esc(t.closeEditor) + '">' + ICON_CLOSE + '</button></div>'
    + '<div class="fm-up__editbody"><div class="fm-up__stagewrap">'
    + '<div class="fm-up__stage" data-fm-stage></div>'
    + '<div class="fm-up__stagetools">'
    + '<button type="button" class="fm-up__stagezoom" data-fm-edit-zoom="-1" aria-label="' + esc(t.zoomOut) + '">&minus;</button>'
    + '<button type="button" class="fm-up__stagezoom" data-fm-edit-zoom="1" aria-label="' + esc(t.zoomIn) + '">+</button>'
    + '<button type="button" class="fm-up__cap-all" data-fm-edit-replace>' + esc(t.replacePhoto) + '</button>'
    + '<button type="button" class="fm-up__cap-rm" data-fm-edit-remove>' + esc(t.remove) + '</button>'
    + '</div>'
    + '<p class="fm-up__stagehint">' + ent(t.stageHint) + '</p></div>'
    + '<div class="fm-up__editside"><div class="fm-up__cap-panel" data-fm-cap hidden>'
    + '<div class="fm-up__cap-head"><span class="fm-up__cap-title">' + esc(t.capTitle) + '</span></div>'
    + '<input type="text" class="fm-up__cap-text" data-fm-cap-text maxlength="40" placeholder="' + esc(t.capPlaceholder) + '" autocomplete="off">'
    + '<p class="fm-up__cap-help">' + esc(t.capHelp) + '</p>'
    + '<div class="fm-up__cap-chips" data-fm-cap-chips></div>'
    + capRow(t.font, '<div class="fm-up__cap-opts" data-fm-cap-fonts></div>')
    + '<input type="text" class="fm-up__cap-text fm-up__cap-text--2" data-fm-cap-text2 maxlength="30" placeholder="' + esc(t.line2Placeholder) + '" autocomplete="off">'
    + capRow(t.line2Font, '<div class="fm-up__cap-opts" data-fm-cap-fonts2></div>')
    + capRow(t.color, '<div class="fm-up__cap-opts" data-fm-cap-colors></div><input type="color" class="fm-up__cap-color" data-fm-cap-color value="#FFFFFF" aria-label="' + esc(t.customColor) + '">')
    + capRow(t.backdrop, '<div class="fm-up__cap-opts" data-fm-cap-band></div><input type="range" class="fm-up__cap-op" data-fm-cap-op min="10" max="95" value="45" aria-label="' + esc(t.backdropOpacity) + '">')
    + capRow(t.backdropHeight, '<div class="fm-up__cap-opts" data-fm-cap-bandsize></div>')
    + '<div class="fm-up__cap-row fm-up__cap-row--effect"><span class="fm-up__cap-lbl">' + esc(t.effect) + '</span><div class="fm-up__cap-opts" data-fm-cap-effect></div><span class="fm-up__cap-hint">' + esc(t.effectHint) + '</span></div>'
    + capRow(t.size, '<input type="range" class="fm-up__cap-op fm-up__cap-size" data-fm-cap-size min="7" max="20" value="12" aria-label="' + esc(t.textSize) + '"><span class="fm-up__cap-hint">' + esc(t.sizeHint) + '</span>')
    + capRow(t.position, '<div class="fm-up__cap-opts" data-fm-cap-pos></div>')
    + '<div class="fm-up__cap-foot">'
    + '<button type="button" class="fm-up__cap-all" data-fm-cap-all>' + esc(t.applyAll) + '</button>'
    + '<button type="button" class="fm-up__cap-rm" data-fm-cap-remove>' + esc(t.removeText) + '</button>'
    + '</div></div></div></div>'
    + '<div class="fm-up__editfoot"><div class="fm-up__editnav">'
    + '<button type="button" class="fm-up__editstep" data-fm-edit-prev>' + ent(t.prev) + '</button>'
    + '<button type="button" class="fm-up__editstep" data-fm-edit-next>' + ent(t.next) + '</button>'
    + '</div><button type="button" class="fm-up__done" data-fm-edit-close>' + esc(t.done) + '</button></div>'
    + '</div></div>'
    + '</div>';
}

if (typeof window !== 'undefined' && window.customElements && !window.customElements.get('photo-uploader')) {
  const MAX_SCALE = 4;

  class PhotoUploader extends HTMLElement {
    connectedCallback() {
      this.map = this._readMap();
      this.t = this._readTexts();
      this._buildShell();
      this.form = this._resolveForm();
      // The buy area the theme re-renders on variant change (see _observe).
      // Dawn's is .product__info-container; another theme names its own.
      const infoSel = this.getAttribute('data-info-selector') || '.product__info-container';
      this.infoContainer = this.closest(infoSel) || document.querySelector(infoSel);
      this.grid = this.querySelector('[data-fm-grid]');
      this.status = this.querySelector('[data-fm-status]');
      this._helpText = this.status ? this.status.textContent.trim() : '';
      this.status2 = this.querySelector('[data-fm-status2]');
      this.modal = this.querySelector('[data-fm-modal]');
      this.openBtn = this.querySelector('[data-fm-open]');
      this.openLabel = this.querySelector('[data-fm-openlabel]');
      this._openLabelDefault = this.openLabel ? this.openLabel.textContent.trim() : this.t.openLabel;
      this.doneBtn = this.querySelector('[data-fm-done]');
      this.upMsg = this.querySelector('[data-fm-upmsg]');
      this.thumbsEl = this.querySelector('[data-fm-thumbs]');
      this.sameToggle = this.querySelector('[data-fm-same]');
      this.capPanel = this.querySelector('[data-fm-cap]');
      this.editor = this.querySelector('[data-fm-edit]');
      this.stage = this.querySelector('[data-fm-stage]');
      this._editSlot = null;
      this._editMark = null;
      this._capSlot = null;
      this._fontsRequested = false;
      this._sameForAll = this.sameToggle ? this.sameToggle.checked : false;
      // True while "use one photo for all" is on only because a bulk pack
      // switched it on, so moving back to a small pack can switch it off again.
      this._autoSame = false;
      this.slots = [];
      this._pack = null;
      this._shape = null;
      this._print = null;
      this.uploadUrl = this.getAttribute('data-upload-url') || '';
      this._ref = window.crypto && crypto.randomUUID ? crypto.randomUUID() : String(Date.now()) + Math.round(Math.random() * 1e6);
      this._allUploaded = false;
      this._bind();
      this._sync();
      this._observe();
    }

    // Dawn re-renders the buy area (price, add button, payment button) on every
    // variant change via the Section Rendering API. That wipes our disabled state
    // and leaves a live Add-to-cart plus an active "Buy with Shop". Watch the info
    // container and re-apply gating whenever it changes.
    _observe() {
      if (!this.infoContainer || typeof MutationObserver === 'undefined') return;
      this._mo = new MutationObserver(() => {
        if (this._reacting || this._gateScheduled) return;
        this._gateScheduled = true;
        requestAnimationFrame(() => {
          this._gateScheduled = false;
          this._reacting = true;
          this.form = this._resolveForm();
          this._sync();
          this._applyGate();
          requestAnimationFrame(() => {
            this._reacting = false;
          });
        });
      });
      this._mo.observe(this.infoContainer, { childList: true, subtree: true });
    }

    // Disable Add-to-cart and hide the dynamic checkout until all photos are in.
    // Re-runnable: always re-resolves the current form node.
    _applyGate() {
      this.form = this._resolveForm();
      if (!this.form) return;
      const filled = isComplete(this.slots);
      const needUpload = !!this.uploadUrl;
      const complete = filled && (!needUpload || this._allUploaded);
      const total = this._pack;
      const addBtn =
        this.form.querySelector('[name="add"]') || this.form.querySelector('button[type="submit"]');
      if (addBtn) {
        addBtn.toggleAttribute('disabled', !complete);
        addBtn.classList.toggle('fm-up-disabled', !complete);
        // Solid, animated state once photos are in (see fresh-chrome.css).
        addBtn.classList.toggle('fm-atc-ready', complete && needUpload);
        this._gateLabel(addBtn, !complete);
      }
      document.querySelectorAll('.shopify-payment-button').forEach((el) => {
        el.classList.toggle('fm-pay-hidden', !complete);
      });
      let prop = this.form.querySelector('[name="properties[Photos]"]');
      if (!prop) {
        prop = document.createElement('input');
        prop.type = 'hidden';
        prop.name = 'properties[Photos]';
        this.form.appendChild(prop);
      }
      prop.value = this._sameForAll
        ? '1 for all ' + total
        : filledCount(this.slots) + ' uploaded';   // shown as "Photos: 6 uploaded", one line in the drawer
      this._writeUrlProps();
    }

    // While photos are missing the add button can read t.addLocked ("Upload
    // photos to continue"); its own label comes back once they are in. A sold
    // out button keeps the theme's text.
    _gateLabel(addBtn, locked) {
      const text = this.t.addLocked;
      if (!text) return;
      const label = addBtn.querySelector('span') || addBtn;
      const cur = label.textContent.trim();
      const vs = window.variantStrings || {};
      if (locked) {
        if (cur === text || (vs.soldOut && cur === vs.soldOut)) return;
        addBtn.dataset.fmAddLabel = cur;
        label.textContent = text;
      } else if (cur === text) {
        label.textContent = addBtn.dataset.fmAddLabel || vs.addToCart || 'Add to cart';
      }
    }

    open() {
      if (this.modal) {
        // Portal the modal to <body> so its position:fixed anchors to the
        // viewport. Dawn's scroll-in animation leaves a transform on an ancestor
        // (.product__info-wrapper), which would otherwise become the containing
        // block for the fixed overlay (modal stuck off-center, top clipped).
        if (this.modal.parentElement !== document.body) {
          document.body.appendChild(this.modal);
        }
        this.modal.hidden = false;
        document.documentElement.style.overflow = 'hidden';
        // Cells had no size while the window was closed: re-fit kept photos.
        this.slots.forEach((s) => { if (s.filled && s.els) this._applyTransform(s, s.els.img); });
      }
    }
    close() {
      this._closeEditor();
      this._closeCaption();
      if (this.modal) {
        this.modal.hidden = true;
        document.documentElement.style.overflow = '';
      }
    }

    // X / Escape. A crop, zoom or caption change after Done leaves the saved
    // links pointing at the old picture, so never just close over it: when the
    // set was already sent and is still complete, send the changes (same as
    // Done); otherwise close and re-gate, which turns Add to cart off and clears
    // the stale links until Done is pressed again. After a failed upload X just
    // closes, so a customer offline is never trapped in the window. Closing
    // while an upload is still running also re-gates (_allUploaded is false
    // until it finishes), so the old links cannot be added in the meantime.
    requestClose() {
      if (this._uploading) {
        this.close();
        this._renderThumbs();
        this._updateStatus();
        return;
      }
      const edited = this.uploadUrl && !this._allUploaded && !this._uploadFailed && isComplete(this.slots) && this.slots.some((s) => s.uploadedUrl);
      if (edited) {
        this._finishUpload();
        return;
      }
      this.close();
      this._renderThumbs();
      this._updateStatus();
    }

    // Dawn renders several forms whose action contains /cart/add (the payment
    // installment form among them). Only the buy form carries the add button,
    // so prefer it explicitly and fall back to one that holds [name="add"].
    _resolveForm() {
      const forms = Array.from(document.querySelectorAll('form[action*="/cart/add"]'));
      return (
        this.closest('form[data-type="add-to-cart-form"]') ||
        document.querySelector('form[data-type="add-to-cart-form"]') ||
        forms.find((f) => f.querySelector('[name="add"]')) ||
        forms[0] ||
        null
      );
    }

    _readMap() {
      try {
        return JSON.parse(this.querySelector('[data-fm-variant-map]').textContent);
      } catch (e) {
        return {};
      }
    }

    // Store texts: defaults overridden by the theme's data-fm-texts JSON.
    _readTexts() {
      let own = {};
      try {
        const el = this.querySelector('[data-fm-texts]');
        if (el) own = JSON.parse(el.textContent) || {};
      } catch (e) {
        own = {};
      }
      return Object.assign({}, UPLOADER_TEXTS, own);
    }

    // Build the bar (only if the theme left it out) and the upload window.
    _buildShell() {
      if (this._shellBuilt) return;
      this._shellBuilt = true;
      if (!this.querySelector('[data-fm-open]')) this.insertAdjacentHTML('afterbegin', uploaderBar(this.t));
      if (this.querySelector('[data-fm-modal]')) return;
      const shapes = Object.keys(this.map || {}).map((k) => String(this.map[k].shape || ''));
      shapes.push(this.getAttribute('data-shape') || '');
      const rectTip = shapes.some((s) => s.indexOf('rect') !== -1);
      const html = uploaderModal(this.t, rectTip);
      const thumbs = this.querySelector('[data-fm-thumbs]');
      if (thumbs) thumbs.insertAdjacentHTML('afterend', html);
      else this.insertAdjacentHTML('beforeend', html);
    }

    _currentVariantId() {
      const input = this.form && this.form.querySelector('[name="id"]');
      return input ? input.value : this.getAttribute('data-variant');
    }

    _info() {
      const id = String(this._currentVariantId() || '');
      const fallbackPrint = this.getAttribute('data-print') || '';
      if (this.map && this.map[id]) {
        const m = this.map[id];
        return { pack: m.pack, shape: m.shape, print: m.print || fallbackPrint };
      }
      return {
        pack: parseInt(this.getAttribute('data-pack'), 10) || 6,
        shape: this.getAttribute('data-shape') || 'square',
        print: fallbackPrint,
      };
    }

    _bind() {
      if (this.form) this.form.addEventListener('change', () => this._sync());
      document.addEventListener('variant:change', () => this._sync());
      if (this.openBtn) this.openBtn.addEventListener('click', () => this.open());
      if (this.doneBtn) this.doneBtn.addEventListener('click', () => this._finishUpload());
      this.querySelectorAll('[data-fm-close]').forEach((el) =>
        el.addEventListener('click', () => this.requestClose())
      );
      document.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape' || !this.modal || this.modal.hidden) return;
        if (this._editSlot) this._closeEditor();
        else this.requestClose();
      });
      this._bindEditor();
      this._bindCaptionPanel();
      if (this.sameToggle) {
        this.sameToggle.addEventListener('change', () => {
          this._sameForAll = this.sameToggle.checked;
          this._autoSame = false;   // the buyer chose; leave it alone from now on
          if (this._sameForAll) {
            const src = this.slots.find((s) => s.filled);
            if (src) this._propagate(src);
          }
          this._applySameMode();
          this._updateStatus();
        });
      }
    }

    _sync() {
      const info = this._info();
      if (info.pack === this._pack && info.shape === this._shape && info.print === this._print) return;
      const shapeChanged =
        (this._shape !== null && this._shape !== info.shape) ||
        (this._print !== null && this._print !== info.print);
      // Bulk packs (25+) are nearly always one photo. Switch "use one photo for
      // all" on when moving to a bulk pack before any photo is added; the
      // buyer can still turn it off. The hint stays visible while bulk.
      const bulk = info.pack >= 25;
      if (bulk && info.pack !== this._pack && this.sameToggle && !this.sameToggle.checked && filledCount(this.slots || []) === 0) {
        this.sameToggle.checked = true;
        this._sameForAll = true;
        this._autoSame = true;
      } else if (!bulk && this._autoSame && this.sameToggle && filledCount(this.slots || []) === 0) {
        // Back to a small pack before any photo was added: undo the automatic
        // switch, so a 6-pack asks for 6 photos again.
        this.sameToggle.checked = false;
        this._sameForAll = false;
        this._autoSame = false;
      }
      const hint = this.querySelector('[data-fm-bulkhint]') || (this.modal && this.modal.querySelector('[data-fm-bulkhint]'));
      if (hint) hint.hidden = !bulk;
      this._pack = info.pack;
      this._shape = info.shape;
      this._print = info.print;
      this._render(shapeChanged);
    }

    _clear(el) {
      while (el.firstChild) el.removeChild(el.firstChild);
    }

    _buildCell(slot, cls) {
      const cell = document.createElement('div');
      cell.className = 'fm-up__slot fm-up__slot--' + cls;

      const file = document.createElement('input');
      file.type = 'file';
      file.accept = 'image/*';
      file.className = 'fm-up__file';
      file.hidden = true;

      const plus = document.createElement('button');
      plus.type = 'button';
      plus.className = 'fm-up__plus';
      plus.textContent = '+';
      plus.addEventListener('click', () => file.click());

      const img = document.createElement('img');
      img.className = 'fm-up__img';
      img.alt = fmt(this.t.photoAlt, { n: slot.index + 1 });
      img.draggable = false;
      img.hidden = true;
      img.addEventListener('load', () => {
        slot.iw = img.naturalWidth;
        slot.ih = img.naturalHeight;
        this._clampSlot(slot);
        this._applyTransform(slot, img);
      });

      const controls = document.createElement('div');
      controls.className = 'fm-up__ctrls';
      controls.hidden = true;
      const zin = this._ctrlBtn('+', this.t.zoomIn, () => this._zoom(slot, cell, img, 0.25));
      const zout = this._ctrlBtn('−', this.t.zoomOut, () => this._zoom(slot, cell, img, -0.25));
      const change = this._ctrlBtn('↻', this.t.changePhoto, () => file.click());
      const textBtn = this._ctrlBtn('\u270E', this.t.editPhoto, () => this._openEditor(slot));
      textBtn.classList.add('fm-up__ctrl--text');
      controls.appendChild(zout);
      controls.appendChild(zin);
      controls.appendChild(change);
      controls.appendChild(textBtn);

      cell.appendChild(file);
      cell.appendChild(plus);
      cell.appendChild(img);
      cell.appendChild(controls);

      // Portrait / landscape switch, rectangles only. Always visible (not
      // hover-gated like the zoom controls) so it is discoverable on touch.
      let layoutEl = null;
      if (cls === 'rect') {
        layoutEl = document.createElement('div');
        layoutEl.className = 'fm-up__layout';
        layoutEl.setAttribute('role', 'group');
        layoutEl.setAttribute('aria-label', this.t.layoutLabel);
        const mk = (val, glyph, label) => {
          const b = document.createElement('button');
          b.type = 'button';
          b.className = 'fm-up__layoutbtn';
          b.dataset.layout = val;
          b.textContent = glyph;
          b.title = label;
          b.setAttribute('aria-label', label);
          b.addEventListener('click', (e) => {
            e.stopPropagation();
            this._setLayout(slot, val);
          });
          return b;
        };
        layoutEl.appendChild(mk('portrait', '\u25AF', this.t.portrait));
        layoutEl.appendChild(mk('landscape', '\u25AD', this.t.landscape));
        layoutEl.addEventListener('pointerdown', (e) => e.stopPropagation());
        cell.appendChild(layoutEl);
      }
      slot.els = { cell, file, plus, img, controls, layoutEl, textBtn, cap: null };
      this._applyLayout(slot);
      file.addEventListener('change', (e) => this._onFile(e, slot, cell, img, plus, controls));
      this._attachPanZoom(slot, cell, img);
      return cell;
    }

    _ctrlBtn(txt, label, fn) {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'fm-up__ctrl';
      b.setAttribute('aria-label', label);
      b.title = label;
      b.textContent = txt;
      b.addEventListener('click', (e) => {
        e.stopPropagation();
        fn();
      });
      // Two quick taps on + must not reach the cell's double-click reset.
      b.addEventListener('dblclick', (e) => e.stopPropagation());
      b.addEventListener('pointerdown', (e) => e.stopPropagation());
      return b;
    }

    // Rebuild the slot grid for the current pack/shape, PRESERVING already-added
    // photos by index: keep the first N when shrinking, keep all + add empties
    // when growing. A shape change invalidates the print composite, so kept
    // photos are marked dirty (re-upload) but the picture itself stays.
    _render(shapeChanged) {
      if (!this.grid) return;
      this._closeEditor();
      const cls = shapeClass(this._shape);
      const n = this._pack;
      const prev = this.slots || [];
      this.grid.style.setProperty('--fm-cols', columnsForCount(n));
      this._clear(this.grid);
      this.slots = [];
      for (let i = 0; i < n; i++) {
        const slot = { index: i, filled: false, url: null, file: null, scale: 1, x: 0, y: 0, uploadedUrl: null, thumb: null, dirty: false, layout: 'portrait', caption: null };
        this.grid.appendChild(this._buildCell(slot, cls));
        this.slots.push(slot);

        const old = prev[i];
        if (old && !shapeChanged && old.layout) {
          slot.layout = old.layout;
          this._applyLayout(slot);
        }
        if (old && old.caption) {
          slot.caption = Object.assign({}, old.caption);
        }
        if (old && old.filled && old.file) {
          slot.file = old.file;
          slot.url = old.url;
          slot.filled = true;
          slot.scale = old.scale;
          slot.x = old.x;
          slot.y = old.y;
          slot.iw = old.iw;
          slot.ih = old.ih;
          if (shapeChanged) {
            slot.dirty = true; // new shape -> must re-composite/upload
          } else {
            slot.uploadedUrl = old.uploadedUrl || null;
            slot.thumb = old.thumb || null;
            slot.dirty = old.dirty || !slot.uploadedUrl;
          }
          slot.els.img.src = slot.url;
          slot.els.img.hidden = false;
          slot.els.plus.hidden = true;
          slot.els.controls.hidden = false;
          slot.els.cell.classList.add('is-filled');
          this._applyTransform(slot, slot.els.img);
          this._renderCaption(slot);
        }
      }

      // "Use one photo for all": fill any newly added empty slots from the kept photo.
      if (this._sameForAll) {
        const src = this.slots.find((s) => s.filled);
        if (src && this.slots.some((s) => !s.filled)) {
          this._propagate(src);
          shareUploadedCopy(this.slots, src);
        }
      }

      this._allUploaded = isComplete(this.slots) && this.slots.every((s) => s.uploadedUrl && !s.dirty);
      this._applySameMode();
      this._renderThumbs();
      this._updateStatus();
    }

    // "Use one photo for all": show a single, larger cell instead of N copies.
    // Every hidden slot still mirrors the first one, so nothing else changes.
    _applySameMode() {
      if (!this.grid) return;
      this.grid.classList.toggle('fm-up__grid--same', !!this._sameForAll);
      if (this._sameForAll && this.slots.length) {
        // Re-fit the visible cell's caption and crop to its new size.
        const first = this.slots[0];
        if (first.els) {
          this._clampSlot(first);
          this._applyTransform(first, first.els.img);
          this._renderCaption(first);
        }
      } else {
        this.slots.forEach((sl) => sl.els && this._renderCaption(sl));
      }
    }

    _onFile(e, slot, cell, img, plus, controls) {
      const f = e.target.files && e.target.files[0];
      if (!f) return;
      if (slot.url) URL.revokeObjectURL(slot.url);
      slot.url = URL.createObjectURL(f);
      slot.file = f;
      slot.filled = true;
      slot.dirty = true;
      this._allUploaded = false;
      slot.scale = 1;
      slot.x = 0;
      slot.y = 0;
      slot.iw = 0;
      slot.ih = 0;
      img.src = slot.url;
      img.hidden = false;
      plus.hidden = true;
      controls.hidden = false;
      cell.classList.add('is-filled');
      this._applyTransform(slot, img);
      // Once the photo's size is known (the cell's own load listener records
      // it first): rectangles take the orientation the photo itself has (the
      // browser applies EXIF rotation to <img>, so naturalWidth/Height are as
      // displayed), then a tall photo starts near its top.
      const url = slot.url;
      img.addEventListener(
        'load',
        () => {
          if (slot.url !== url) return;
          if (shapeClass(this._shape) === 'rect') {
            this._setLayout(slot, layoutForPhoto(img.naturalWidth, img.naturalHeight));
          }
          slot.y = startOffsetY(slot.iw, slot.ih, cell.clientWidth, cell.clientHeight);
          this._applyTransform(slot, img);
          this._propagate(slot);
        },
        { once: true }
      );
      this._propagate(slot);
      this._updateStatus();
    }

    _applyLayout(slot) {
      const els = slot.els;
      if (!els) return;
      const land = slot.layout === 'landscape' && shapeClass(this._shape) === 'rect';
      els.cell.classList.toggle('fm-up__slot--land', land);
      if (els.layoutEl) {
        els.layoutEl.querySelectorAll('.fm-up__layoutbtn').forEach((b) => {
          const on = b.dataset.layout === (land ? 'landscape' : 'portrait');
          b.classList.toggle('is-on', on);
          b.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
      }
    }

    // Flip one slot between portrait and landscape. The pan is reset because
    // the frame changed shape; zoom is kept. Fans out in same-for-all mode.
    _setLayout(slot, layout) {
      if (shapeClass(this._shape) !== 'rect') return;
      if (slot.layout === layout) return;
      slot.layout = layout;
      slot.x = 0;
      slot.y = 0;
      if (slot.filled) {
        slot.dirty = true;
        this._allUploaded = false;
      }
      this._applyLayout(slot);
      if (slot.els) this._applyTransform(slot, slot.els.img);
      this._renderCaption(slot);
      this._propagate(slot);
      this._updateStatus();
    }

    // ---------- captions ----------

    // Google Fonts for the caption looks, loaded once, only when someone opens
    // the caption panel, so the product page itself never pays for them.
    _ensureFonts(fontId) {
      if (!this._fontsRequested) {
        this._fontsRequested = true;
        const fams = CAPTION_FONTS.filter((f) => f.gf).map((f) => 'family=' + f.gf).join('&');
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = 'https://fonts.googleapis.com/css2?' + fams + '&display=swap';
        document.head.appendChild(link);
      }
      if (!document.fonts || !document.fonts.load) return Promise.resolve();
      const wanted = fontId ? [captionFont(fontId)] : CAPTION_FONTS;
      const loads = wanted.map((f) => document.fonts.load(f.weight + ' 40px ' + f.family).catch(() => null));
      return Promise.race([Promise.all(loads), new Promise((r) => setTimeout(r, 3000))]);
    }

    // Draw (or clear) the on-slot preview. Same maths as the print file, scaled
    // to the cell, so what they see is what gets pressed.
    _renderCaption(slot) {
      const els = slot.els;
      if (!els) return;
      const cap = slot.caption;
      const t1 = cap && cap.text ? cap.text.trim() : '';
      const t2 = cap && cap.text2 ? cap.text2.trim() : '';
      const has = !!((t1 || t2) && slot.filled);
      if (els.textBtn) els.textBtn.classList.toggle('is-on', !!(t1 || t2));
      if (!has) {
        if (els.cap) {
          els.cap.remove();
          els.cap = null;
        }
        els.cell.classList.remove('fm-up__slot--cap-top');
        return;
      }
      if (!els.cap) {
        els.cap = document.createElement('div');
        els.cap.className = 'fm-up__cap';
        els.cap.setAttribute('aria-hidden', 'true');
        els.cell.appendChild(els.cap);
      }
      const W = els.cell.clientWidth || 1;
      const H = els.cell.clientHeight || 1;
      const mctx = (this._mctx = this._mctx || document.createElement('canvas').getContext('2d'));
      const band = captionBandColor(cap);
      const effect = captionEffect(cap);
      const el = els.cap;
      el.style.paddingTop = '0';
      el.style.paddingBottom = '0';
      el.style.lineHeight = '1';
      el.style.color = cap.color || '#FFFFFF';
      el.style.background = band || 'transparent';
      // Keep the zoom / swap buttons at the other end from the band.
      els.cell.classList.toggle('fm-up__slot--cap-top', cap.pos === 'top');
      // Same layout as the print file; each line is its own span, its em box
      // centred on the line's y, nudged by the measured ink offset.
      const paint = () => {
        const L = captionLayout(cap, W, H, shapeClass(this._shape), (t, fontId, px) => {
          mctx.font = captionFontCss(px, fontId);
          const mt = mctx.measureText(t);
          return { w: mt.width, asc: mt.actualBoundingBoxAscent || px * 0.7, desc: mt.actualBoundingBoxDescent || 0 };
        });
        if (!L) return;
        el.style.top = L.bandTop + 'px';
        el.style.height = L.bandH + 'px';
        while (el.children.length > L.lines.length) el.lastElementChild.remove();
        L.lines.forEach((ln, i) => {
          let span = el.children[i];
          if (!span) {
            span = document.createElement('span');
            span.className = 'fm-up__cap-line';
            el.appendChild(span);
          }
          const f = captionFont(ln.font);
          span.textContent = ln.text;
          span.style.fontFamily = f.family;
          span.style.fontWeight = f.weight;
          span.style.fontSize = ln.px + 'px';
          span.style.top = (ln.y - L.bandTop - ln.px / 2) + 'px';
          span.style.transform = 'translateY(' + captionInkShift(mctx, ln.text, captionFontCss(ln.px, ln.font)) + 'px)';
          span.style.textShadow = captionShadowCss(ln.px, effect, cap.color || '#FFFFFF');
        });
      };
      paint();
      // Fonts may still be loading on first use; re-measure once they land.
      Promise.all([t1 ? this._ensureFonts(cap.font) : null, t2 ? this._ensureFonts(cap.font2 || 'oswald') : null]).then(() => {
        if (slot.caption === cap && els.cap === el) paint();
      });
    }

    _captionChanged(slot) {
      if (slot.filled) {
        slot.dirty = true;
        this._allUploaded = false;
      }
      this._renderCaption(slot);
      if (this._sameForAll) this._propagate(slot);
      this._updateStatus();
    }

    _openCaption(slot) {
      if (!this.capPanel) return;
      if (!slot.caption) slot.caption = defaultCaption('');
      this._capSlot = slot;
      this._ensureFonts();
      this._syncCaptionPanel();
      this.capPanel.hidden = false;
      this.slots.forEach((s) => s.els && s.els.cell.classList.toggle('is-editing', s === slot));
    }

    _closeCaption() {
      if (!this.capPanel) return;
      const slot = this._capSlot;
      if (slot && slot.caption && !slot.caption.text.trim() && !(slot.caption.text2 || '').trim()) slot.caption = null;
      if (slot) this._renderCaption(slot);
      this.capPanel.hidden = true;
      this._capSlot = null;
      this.slots.forEach((s) => s.els && s.els.cell.classList.remove('is-editing'));
    }

    _seg(container, items, getVal, onPick, render) {
      if (!container) return;
      this._clear(container);
      items.forEach((it) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'fm-up__seg';
        b.dataset.val = it.id;
        if (render) render(b, it);
        else b.textContent = it.label;
        b.addEventListener('click', () => {
          onPick(it);
          container.querySelectorAll('.fm-up__seg').forEach((x) => x.classList.toggle('is-on', x.dataset.val === String(getVal())));
        });
        container.appendChild(b);
      });
      container.querySelectorAll('.fm-up__seg').forEach((x) => x.classList.toggle('is-on', x.dataset.val === String(getVal())));
    }

    _bindCaptionPanel() {
      const p = this.capPanel;
      if (!p) return;
      const q = (sel) => p.querySelector(sel);
      const cur = () => (this._capSlot && this._capSlot.caption) || null;
      const change = () => this._capSlot && this._captionChanged(this._capSlot);

      const text = q('[data-fm-cap-text]');
      if (text) {
        text.addEventListener('input', () => {
          const c = cur();
          if (!c) return;
          c.text = text.value.slice(0, 40);
          change();
        });
      }
      const text2 = q('[data-fm-cap-text2]');
      if (text2) {
        text2.addEventListener('input', () => {
          const c = cur();
          if (!c) return;
          c.text2 = text2.value.slice(0, CAPTION_LINE2_MAX);
          change();
        });
      }
      // Second line font, rendered in its own face
      this._seg(q('[data-fm-cap-fonts2]'), CAPTION_FONTS, () => (cur() ? cur().font2 || 'oswald' : ''), (it) => {
        const c = cur();
        if (!c) return;
        c.font2 = it.id;
        this._ensureFonts(it.id).then(change);
        change();
      }, (b, it) => {
        b.textContent = it.label;
        b.style.fontFamily = it.family;
        b.style.fontWeight = it.weight;
        b.classList.add('fm-up__seg--font');
      });
      // Text effect (used when there is no backdrop)
      const effectRow = q('[data-fm-cap-effect]');
      this._seg(effectRow, CAPTION_EFFECTS, () => (cur() ? cur().effect || 'shadow' : ''), (it) => {
        const c = cur();
        if (!c) return;
        c.effect = it.id;
        change();
      });
      // Quick phrases
      this._seg(q('[data-fm-cap-chips]'), CAPTION_PHRASES.map((t) => ({ id: t, label: t })), () => (cur() ? cur().text : ''), (it) => {
        const c = cur();
        if (!c) return;
        c.text = it.id;
        if (text) text.value = it.id;
        change();
      });
      // Fonts, rendered in their own face
      this._seg(q('[data-fm-cap-fonts]'), CAPTION_FONTS, () => (cur() ? cur().font : ''), (it) => {
        const c = cur();
        if (!c) return;
        c.font = it.id;
        this._ensureFonts(it.id).then(change);
        change();
      }, (b, it) => {
        b.textContent = it.label;
        b.style.fontFamily = it.family;
        b.style.fontWeight = it.weight;
        b.classList.add('fm-up__seg--font');
      });
      // Colours
      const colorInput = q('[data-fm-cap-color]');
      this._seg(q('[data-fm-cap-colors]'), CAPTION_COLORS, () => (cur() ? cur().color.toUpperCase() : ''), (it) => {
        const c = cur();
        if (!c) return;
        c.color = it.hex;
        if (colorInput) colorInput.value = it.hex;
        change();
      }, (b, it) => {
        b.dataset.val = it.hex;
        b.classList.add('fm-up__seg--swatch');
        b.style.background = it.hex;
        b.title = it.id;
        b.setAttribute('aria-label', it.id);
      });
      if (colorInput) {
        colorInput.addEventListener('input', () => {
          const c = cur();
          if (!c) return;
          c.color = colorInput.value.toUpperCase();
          p.querySelectorAll('[data-fm-cap-colors] .fm-up__seg').forEach((x) => x.classList.toggle('is-on', x.dataset.val === c.color));
          change();
        });
      }
      // Backdrop band + opacity
      const op = q('[data-fm-cap-op]');
      this._seg(q('[data-fm-cap-band]'), [
        { id: 'none', label: 'None' },
        { id: 'dark', label: 'Dark' },
        { id: 'white', label: 'White' },
      ], () => (cur() ? cur().band : ''), (it) => {
        const c = cur();
        if (!c) return;
        c.band = it.id;
        if (op) op.disabled = it.id === 'none';
        if (effectRow) effectRow.classList.toggle('is-off', it.id !== 'none');
        change();
      });
      if (op) {
        op.addEventListener('input', () => {
          const c = cur();
          if (!c) return;
          c.bandOpacity = Math.max(0.1, Math.min(0.95, parseInt(op.value, 10) / 100));
          change();
        });
      }
      this._seg(q('[data-fm-cap-bandsize]'), [
        { id: 'sm', label: 'Small' },
        { id: 'md', label: 'Medium' },
        { id: 'lg', label: 'Large' },
      ], () => (cur() ? cur().bandSize || 'md' : ''), (it) => {
        const c = cur();
        if (!c) return;
        c.bandSize = it.id;
        change();
      });
      // Size slider (percent of magnet height) + position
      const sz = q('[data-fm-cap-size]');
      if (sz) {
        sz.addEventListener('input', () => {
          const c = cur();
          if (!c) return;
          c.size = parseInt(sz.value, 10);
          change();
        });
      }
      this._seg(q('[data-fm-cap-pos]'), [
        { id: 'top', label: 'Top' },
        { id: 'middle', label: 'Middle' },
        { id: 'bottom', label: 'Bottom' },
      ], () => (cur() ? cur().pos : ''), (it) => {
        const c = cur();
        if (!c) return;
        c.pos = it.id;
        change();
      });
      // Footer actions
      const all = q('[data-fm-cap-all]');
      if (all) {
        all.addEventListener('click', () => {
          const c = cur();
          if (!c) return;
          this.slots.forEach((s) => {
            if (s === this._capSlot) return;
            s.caption = Object.assign({}, c);
            if (s.filled) {
              s.dirty = true;
              this._allUploaded = false;
            }
            this._renderCaption(s);
          });
          this._updateStatus();
          all.textContent = this.t.appliedAll;
          setTimeout(() => (all.textContent = this.t.applyAll), 1500);
        });
      }
      const rm = q('[data-fm-cap-remove]');
      if (rm) {
        rm.addEventListener('click', () => {
          const slot = this._capSlot;
          if (!slot) return;
          slot.caption = null;
          if (text) text.value = '';
          if (text2) text2.value = '';
          this._captionChanged(slot);
          // Still in the editor: start a fresh, empty caption so typing and
          // every option button work again (they all edit slot.caption, and
          // with none they silently did nothing). An empty caption is dropped
          // again when the editor closes.
          if (this._editSlot) this._openCaption(slot);
          else this._closeCaption();
        });
      }
      const done = q('[data-fm-cap-close]');
      if (done) done.addEventListener('click', () => this._closeCaption());
    }

    // Push the current slot's caption into the panel controls.
    _syncCaptionPanel() {
      const p = this.capPanel;
      const c = this._capSlot && this._capSlot.caption;
      if (!p || !c) return;
      const n = p.querySelector('[data-fm-cap-n]');
      if (n) n.textContent = String(this._capSlot.index + 1);
      const text = p.querySelector('[data-fm-cap-text]');
      if (text) text.value = c.text;
      const text2 = p.querySelector('[data-fm-cap-text2]');
      if (text2) text2.value = c.text2 || '';
      const effectRow = p.querySelector('[data-fm-cap-effect]');
      if (effectRow) effectRow.classList.toggle('is-off', c.band !== 'none');
      const colorInput = p.querySelector('[data-fm-cap-color]');
      if (colorInput) colorInput.value = c.color;
      const op = p.querySelector('[data-fm-cap-op]');
      if (op) {
        op.value = String(Math.round(c.bandOpacity * 100));
        op.disabled = c.band === 'none';
      }
      const sz = p.querySelector('[data-fm-cap-size]');
      if (sz) sz.value = String(Math.round(captionSizeFrac(c.size) * 100));
      const on = (sel, val) => p.querySelectorAll(sel + ' .fm-up__seg').forEach((x) => x.classList.toggle('is-on', x.dataset.val === String(val)));
      on('[data-fm-cap-chips]', c.text);
      on('[data-fm-cap-fonts]', c.font);
      on('[data-fm-cap-fonts2]', c.font2 || 'oswald');
      on('[data-fm-cap-effect]', c.effect || 'shadow');
      on('[data-fm-cap-colors]', c.color.toUpperCase());
      on('[data-fm-cap-band]', c.band);
      on('[data-fm-cap-bandsize]', c.bandSize || 'md');
      on('[data-fm-cap-pos]', c.pos);
    }

    // The <img> is sized to the photo's cover box (in % of the cell, so it
    // follows the cell when it resizes), not to the cell: that way panning can
    // reach the parts of a tall or wide photo that cover would cut off. Until
    // the photo's size is known it falls back to object-fit: cover in the cell.
    _applyTransform(slot, img) {
      const cell = slot.els && slot.els.cell;
      const c = cell && coverSize(slot.iw, slot.ih, cell.clientWidth, cell.clientHeight);
      if (c) {
        const w = (c[0] / cell.clientWidth) * 100;
        const h = (c[1] / cell.clientHeight) * 100;
        img.style.width = w + '%';
        img.style.height = h + '%';
        img.style.left = (100 - w) / 2 + '%';
        img.style.top = (100 - h) / 2 + '%';
        img.style.right = 'auto';
        img.style.bottom = 'auto';
      } else {
        img.style.width = img.style.height = img.style.left = img.style.top = img.style.right = img.style.bottom = '';
      }
      img.style.transform =
        'translate(' + slot.x + 'px,' + slot.y + 'px) scale(' + slot.scale + ')';
    }

    // Keep a slot's pan inside what its photo can cover at the current zoom.
    _clampSlot(slot) {
      const cell = slot.els && slot.els.cell;
      if (!cell) return;
      const cw = cell.clientWidth;
      const ch = cell.clientHeight;
      const c = coverSize(slot.iw, slot.ih, cw, ch);
      slot.x = clampOffset(slot.x || 0, slot.scale, cw, c ? c[0] : cw);
      slot.y = clampOffset(slot.y || 0, slot.scale, ch, c ? c[1] : ch);
    }

    // In "use one photo for all" mode, copy a slot's photo + crop to every slot.
    _propagate(src) {
      if (!this._sameForAll || !src.filled) return;
      this.slots.forEach((s) => {
        if (s === src || !s.els) return;
        s.url = src.url;
        s.file = src.file;
        s.filled = true;
        s.dirty = true;
        s.scale = src.scale;
        s.x = src.x;
        s.y = src.y;
        if (s.layout !== src.layout) {
          s.layout = src.layout;
          this._applyLayout(s);
        }
        s.caption = src.caption ? Object.assign({}, src.caption) : null;
        this._renderCaption(s);
        s.els.img.src = src.url;
        s.els.img.hidden = false;
        s.els.plus.hidden = true;
        s.els.controls.hidden = false;
        s.els.cell.classList.add('is-filled');
        this._applyTransform(s, s.els.img);
      });
      this._allUploaded = false;
    }

    _zoom(slot, cell, img, delta) {
      this._setScale(slot, cell, img, slot.scale + delta);
    }

    _setScale(slot, cell, img, value) {
      if (!slot.filled) return;
      this._allUploaded = false;
      slot.dirty = true;
      slot.scale = Math.min(MAX_SCALE, Math.max(1, value));
      this._clampSlot(slot);
      this._applyTransform(slot, img);
      this._propagate(slot);
    }

    _attachPanZoom(slot, cell, img) {
      let dragging = false;
      let moved = false;
      let sx = 0;
      let sy = 0;
      let ox = 0;
      let oy = 0;
      // Two-finger pinch: every active pointer is tracked; with two down, the
      // change in distance between them scales the photo.
      const pts = new Map();
      let pinch = null;
      const dist = () => {
        const a = Array.from(pts.values());
        return Math.hypot(a[0].x - a[1].x, a[0].y - a[1].y) || 1;
      };
      cell.addEventListener('pointerdown', (e) => {
        if (!slot.filled || e.target.closest('.fm-up__ctrl')) return;
        // Touch in the grid: let a swipe scroll the uploader (CSS gives the slot
        // touch-action: pan-y) and treat a tap as "open the big editor". Dragging and
        // pinching a photo on a phone happen in the editor, where the cell is staged.
        if (e.pointerType === 'touch' && !cell.classList.contains('is-staged')) {
          dragging = false;
          moved = false;
          return;
        }
        pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
        try {
          cell.setPointerCapture(e.pointerId);
        } catch (err) {}
        if (pts.size >= 2) {
          dragging = false;
          moved = true;
          pinch = { dist: dist(), scale: slot.scale };
          return;
        }
        dragging = true;
        moved = false;
        sx = e.clientX;
        sy = e.clientY;
        ox = slot.x;
        oy = slot.y;
      });
      cell.addEventListener('pointermove', (e) => {
        if (pts.has(e.pointerId)) pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
        if (pinch && pts.size >= 2) {
          this._setScale(slot, cell, img, pinch.scale * (dist() / pinch.dist));
          return;
        }
        if (!dragging) return;
        if (!moved && Math.abs(e.clientX - sx) < 4 && Math.abs(e.clientY - sy) < 4) return;
        moved = true;
        this._allUploaded = false;
        slot.dirty = true;
        slot.x = ox + (e.clientX - sx);
        slot.y = oy + (e.clientY - sy);
        this._clampSlot(slot);
        this._applyTransform(slot, img);
        this._propagate(slot);
      });
      const end = (e) => {
        if (e) pts.delete(e.pointerId);
        if (pts.size < 2) pinch = null;
        dragging = false;
      };
      cell.addEventListener('pointerup', (e) => {
        const wasDrag = moved;
        end(e);
        // A tap (no drag) on a photo in the grid opens the big editor.
        if (wasDrag || !slot.filled || cell.classList.contains('is-staged')) return;
        if (e.target.closest('.fm-up__ctrl, .fm-up__layout')) return;
        this._openEditor(slot);
      });
      cell.addEventListener('pointercancel', end);
      cell.addEventListener('lostpointercapture', (e) => end(e));
      cell.addEventListener(
        'wheel',
        (e) => {
          if (!slot.filled) return;
          e.preventDefault();
          this._zoom(slot, cell, img, e.deltaY < 0 ? 0.2 : -0.2);
        },
        { passive: false }
      );
      cell.addEventListener('dblclick', (e) => {
        if (!slot.filled) return;
        if (e.target.closest('.fm-up__ctrl, .fm-up__layout')) return;
        this._allUploaded = false;
        slot.dirty = true;
        slot.scale = 1;
        slot.x = 0;
        slot.y = 0;
        this._applyTransform(slot, img);
      });
    }

    // ---------- editor (big single-magnet view) ----------

    // The editor shows ONE magnet large: the real grid cell is moved into the
    // stage (so pan / zoom / caption code keeps working on the same element),
    // and moved back on close. Pan offsets are in cell pixels, so they are
    // scaled with the cell on the way in and out.
    _openEditor(slot) {
      if (!this.editor || !this.stage || !slot.els) return;
      if (!slot.filled) {
        slot.els.file.click();
        return;
      }
      if (this._editSlot === slot) return;
      if (this._editSlot) this._closeEditor();
      const cell = slot.els.cell;
      const w0 = cell.clientWidth || 1;
      this.editor.hidden = false;
      this._editMark = document.createComment('fm-edit');
      cell.parentNode.insertBefore(this._editMark, cell);
      this.stage.appendChild(cell);
      cell.classList.add('is-staged');
      this._editSlot = slot;
      this._rescaleSlot(slot, w0);
      this._openCaption(slot);
      this._renderCaption(slot);
      this._syncEditorNav();
    }

    _closeEditor() {
      const slot = this._editSlot;
      if (!slot) return;
      const cell = slot.els.cell;
      const w1 = cell.clientWidth || 1;
      this._closeCaption();
      cell.classList.remove('is-staged');
      if (this._editMark && this._editMark.parentNode) {
        this._editMark.parentNode.insertBefore(cell, this._editMark);
        this._editMark.remove();
      } else if (this.grid) {
        this.grid.appendChild(cell);
      }
      this._editMark = null;
      this._editSlot = null;
      this.editor.hidden = true;
      this._rescaleSlot(slot, w1);
      this._renderCaption(slot);
      this._propagate(slot);
    }

    // Scale the pan offsets from the cell's previous width to its current one.
    _rescaleSlot(slot, prevW) {
      const cell = slot.els.cell;
      const w = cell.clientWidth || prevW;
      const r = prevW ? w / prevW : 1;
      slot.x = (slot.x || 0) * r;
      slot.y = (slot.y || 0) * r;
      this._clampSlot(slot);
      this._applyTransform(slot, slot.els.img);
    }

    _syncEditorNav() {
      const ed = this.editor;
      const slot = this._editSlot;
      if (!ed || !slot) return;
      const n = ed.querySelector('[data-fm-edit-n]');
      const t = ed.querySelector('[data-fm-edit-total]');
      if (n) n.textContent = String(slot.index + 1);
      if (t) t.textContent = String(this.slots.length);
      const prev = ed.querySelector('[data-fm-edit-prev]');
      const next = ed.querySelector('[data-fm-edit-next]');
      const single = this._sameForAll || this.slots.length < 2;
      if (prev) prev.hidden = single;
      if (next) next.hidden = single;
      if (prev) prev.disabled = slot.index === 0;
      if (next) next.disabled = slot.index >= this.slots.length - 1;
    }

    // Move to the neighbouring magnet without leaving the editor.
    _editStep(dir) {
      const slot = this._editSlot;
      if (!slot) return;
      const target = this.slots[slot.index + dir];
      if (!target) return;
      this._closeEditor();
      if (target.filled) this._openEditor(target);
      else target.els.file.click();
    }

    // Clear a magnet (all of them in "one photo for all" mode).
    _removePhoto(slot) {
      const targets = this._sameForAll ? this.slots : [slot];
      const url = slot.url;
      this._closeEditor();
      targets.forEach((s) => {
        s.url = null;
        s.file = null;
        s.filled = false;
        s.uploadedUrl = null;
        s.thumb = null;
        s.dirty = false;
        s.scale = 1;
        s.x = 0;
        s.y = 0;
        s.caption = null;
        if (s.els) {
          s.els.img.removeAttribute('src');
          s.els.img.hidden = true;
          s.els.plus.hidden = false;
          s.els.controls.hidden = true;
          s.els.cell.classList.remove('is-filled');
          this._applyTransform(s, s.els.img);
          this._renderCaption(s);
        }
      });
      if (url && !this.slots.some((s) => s.url === url)) URL.revokeObjectURL(url);
      this._allUploaded = false;
      this._renderThumbs();
      this._updateStatus();
    }

    _bindEditor() {
      const ed = this.editor;
      if (!ed) return;
      ed.querySelectorAll('[data-fm-edit-close]').forEach((b) => b.addEventListener('click', () => this._closeEditor()));
      ed.querySelectorAll('[data-fm-edit-zoom]').forEach((b) =>
        b.addEventListener('click', () => {
          const slot = this._editSlot;
          if (!slot) return;
          this._zoom(slot, slot.els.cell, slot.els.img, parseFloat(b.dataset.fmEditZoom) > 0 ? 0.25 : -0.25);
        })
      );
      const rep = ed.querySelector('[data-fm-edit-replace]');
      if (rep) rep.addEventListener('click', () => this._editSlot && this._editSlot.els.file.click());
      const rm = ed.querySelector('[data-fm-edit-remove]');
      if (rm) rm.addEventListener('click', () => this._editSlot && this._removePhoto(this._editSlot));
      const prev = ed.querySelector('[data-fm-edit-prev]');
      if (prev) prev.addEventListener('click', () => this._editStep(-1));
      const next = ed.querySelector('[data-fm-edit-next]');
      if (next) next.addEventListener('click', () => this._editStep(1));
    }

    _updateStatus() {
      const total = this._pack;
      const complete = isComplete(this.slots);
      let label;
      if (this._sameForAll) {
        label = complete
          ? fmt(this.t.oneForAll, { total })
          : this._helpText;
      } else {
        label = fmt(this.t.countAdded, { n: filledCount(this.slots), total });
      }
      const started = filledCount(this.slots) > 0;
      if (this.status) {
        this.status.textContent = started ? label : this._helpText;
        this.status.classList.toggle('is-complete', complete);
      }
      if (this.status2) {
        this.status2.textContent = label;
        this.status2.classList.toggle('is-complete', complete);
      }
      if (this.doneBtn) this.doneBtn.disabled = !complete;
      if (this.openBtn) this.openBtn.classList.toggle('is-started', started);
      if (this.openLabel) {
        this.openLabel.textContent = started
          ? complete
            ? this.t.editPictures
            : this.t.continueUploading
          : this._openLabelDefault;
      }
      this._applyGate();
    }

    // Print canvas px at 300 DPI. Driven by the product's custom.print_size metafield
    // ("WxH") so sizes that share a shape (2" vs 2.5" square) print correctly; falls
    // back to the original per-shape defaults if the metafield is missing.
    _printSize() {
      return printSize(this._print, this._shape);
    }

    // Reproduce the on-screen crop (object-fit cover + zoom + pan) onto a
    // print-resolution canvas and return a JPEG blob.
    async _compositeSlot(slot) {
      const land = slot.layout === 'landscape' && shapeClass(this._shape) === 'rect';
      const size = slotPrintSize(this._printSize(), slot.layout, this._shape);
      const pw = size[0];
      const ph = size[1];
      const cell = slot.els.cell;
      const cw = cell.clientWidth || 1;
      const ch = cell.clientHeight || 1;
      const kx = pw / cw;
      const ky = ph / ch;
      const bitmap = await decodeImage(slot.file);
      const iw = bitmap.width;
      const ih = bitmap.height;
      const cover = Math.max(pw / iw, ph / ih);
      const s = slot.scale || 1;
      const drawW = iw * cover * s;
      const drawH = ih * cover * s;
      const cx = pw / 2 + (slot.x || 0) * kx;
      const cy = ph / 2 + (slot.y || 0) * ky;
      const canvas = document.createElement('canvas');
      canvas.width = pw;
      canvas.height = ph;
      const ctx = canvas.getContext('2d');
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, pw, ph);
      ctx.drawImage(bitmap.image || bitmap, cx - drawW / 2, cy - drawH / 2, drawW, drawH);
      if (bitmap.close) bitmap.close();
      if (slot.caption && slot.caption.text && slot.caption.text.trim()) {
        await this._ensureFonts(slot.caption.font);
        if (slot.caption.text2 && slot.caption.text2.trim()) await this._ensureFonts(slot.caption.font2 || 'oswald');
        drawCaption(ctx, pw, ph, slot.caption, shapeClass(this._shape));
      }
      // Small thumbnail from the same composite — instant on-page preview, no network.
      const tc = document.createElement('canvas');
      tc.width = 160;
      tc.height = Math.round((160 * ph) / pw);
      tc.getContext('2d').drawImage(canvas, 0, 0, tc.width, tc.height);
      slot.thumb = tc.toDataURL('image/jpeg', 0.82);
      // A landscape rectangle is uploaded turned 90 degrees clockwise into the
      // standard portrait print file, so the worker, the print sheet (portrait
      // cells) and the admin proofs all see an ordinary 2 x 3. The magnet is the
      // same piece either way; it just gets turned when it goes on the fridge.
      let out = canvas;
      if (land) {
        out = document.createElement('canvas');
        out.width = ph;
        out.height = pw;
        const octx = out.getContext('2d');
        octx.imageSmoothingEnabled = true;
        octx.imageSmoothingQuality = 'high';
        octx.translate(out.width, 0);
        octx.rotate(Math.PI / 2);
        octx.drawImage(canvas, 0, 0);
      }
      const blob = await new Promise((resolve) => {
        try { out.toBlob(resolve, 'image/jpeg', 0.95); } catch (e) { resolve(null); }
      });
      return blob || dataUrlToBlob(out.toDataURL('image/jpeg', 0.95));
    }

    // Build one print file, tagging any failure as a device-side "prepare" error.
    async _prepare(slot) {
      try {
        return await this._compositeSlot(slot);
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        err.stage = 'prepare';
        throw err;
      }
    }

    async _uploadBlob(blob) {
      return withRetry(async () => {
        const resp = await fetch(this.uploadUrl, {
          method: 'POST',
          headers: uploadHeaders(this._ref, this._info().pack, this._sameForAll, visitorId()),
          body: blob,
        });
        if (!resp.ok) { const err = new Error('HTTP ' + resp.status); err.status = resp.status; throw err; }
        const data = await resp.json();
        if (!data || !data.url) throw new Error('No URL returned');
        return data.url;
      });
    }

    // Send each photo's original (see originalBlob) next to its print file, in
    // the background after the print files: Add to cart never waits for it, and
    // a failure is ignored because the print file is what the order needs.
    async _uploadOriginals() {
      if (this._origBusy || !this.uploadUrl) return;
      this._origBusy = true;
      try {
        for (;;) {
          const slot = this.slots.find((s) => s.file && s.uploadedUrl && s.origFor !== s.uploadedUrl && s.origFailed !== s.uploadedUrl);
          if (!slot) break;
          const done = slot.uploadedUrl;
          try {
            const blob = await originalBlob(slot.file);
            await withRetry(async () => {
              const headers = uploadHeaders(this._ref, this._info().pack, this._sameForAll, visitorId());
              headers['X-Original-Of'] = uploadKey(done);
              const resp = await fetch(this.uploadUrl, { method: 'POST', headers, body: blob });
              if (!resp.ok) { const err = new Error('HTTP ' + resp.status); err.status = resp.status; throw err; }
            });
            this.slots.forEach((s) => { if (s.uploadedUrl === done) s.origFor = done; });
          } catch (e) {
            this.slots.forEach((s) => { if (s.uploadedUrl === done) s.origFailed = done; });
          }
        }
      } finally {
        this._origBusy = false;
      }
    }

    // Tell the shop why an upload failed (device, step, error), so a customer's
    // "it won't upload" can be traced. Plain text keeps it a simple request
    // (no CORS preflight); any failure here is ignored.
    _reportFailure(stage, err) {
      try {
        if (!this.uploadUrl) return;
        const note = JSON.stringify({ ref: this._ref, stage, error: String((err && err.message) || err).slice(0, 300),
          shape: this._shape, pack: this._info().pack, photos: this.slots.length, page: location.pathname });
        fetch(new URL('/api/client-error', this.uploadUrl).href, { method: 'POST', body: note, keepalive: true,
          headers: { 'Content-Type': 'text/plain' } }).catch(() => {});
      } catch (e) { /* never let reporting break the page */ }
    }

    // Triggered by the Done button: composite every slot, upload to the Worker,
    // save the returned links as line-item properties, then close.
    async _finishUpload() {
      if (!isComplete(this.slots)) return;
      if (!this.uploadUrl || this._allUploaded) {
        this.close();
        return;
      }
      if (this._uploading) return;
      this._uploading = true;
      this._uploadFailed = false;
      this._setBusy(true, this.t.preparing);
      // Nothing old may be added to the cart while the new set is on its way.
      this._applyGate();
      try {
        if (this._sameForAll) {
          const src = this.slots.find((s) => s.filled);
          // Upload the shared photo once, and only if it changed or was never sent.
          if (!src.uploadedUrl || src.dirty) {
            src.uploadedUrl = await this._uploadBlob(await this._prepare(src));
          }
          this.slots.forEach((s) => {
            s.uploadedUrl = src.uploadedUrl;
            s.thumb = src.thumb;
            s.dirty = false;
          });
        } else {
          // Upload only slots whose photo or crop changed (or were never sent).
          const pending = this.slots.filter((s) => s.dirty || !s.uploadedUrl);
          for (let i = 0; i < pending.length; i++) {
            this._setBusy(true, fmt(this.t.uploadingN, { n: i + 1, total: pending.length }));
            const slot = pending[i];
            slot.uploadedUrl = await this._uploadBlob(await this._prepare(slot));
            slot.dirty = false;
          }
        }
        this._allUploaded = true;
        this._setBusy(false);
        this._applyGate();
        this._renderThumbs();
        this.close();
        this._uploadOriginals();
      } catch (e) {
        this._allUploaded = false;
        this._uploadFailed = true;
        // Building the print file on the device is a different failure from
        // sending it, and needs a different hint for the customer.
        const stage = e && e.stage === 'prepare' ? 'prepare' : 'send';
        this._setBusy(false, stage === 'prepare' ? this.t.prepareFailed : this.t.uploadFailed, true);
        this._reportFailure(stage, e);
        // Closed mid-upload: the page must not keep offering Add to cart.
        if (this.modal && this.modal.hidden) {
          this._renderThumbs();
          this._updateStatus();
        }
      } finally {
        this._uploading = false;
      }
    }

    _setBusy(busy, msg, isError) {
      if (this.doneBtn) {
        this.doneBtn.disabled = busy || !isComplete(this.slots);
        this.doneBtn.textContent = busy ? this.t.uploading : this.t.done;
      }
      if (this.upMsg) {
        if (msg) {
          this.upMsg.textContent = msg;
          this.upMsg.hidden = false;
          this.upMsg.classList.toggle('is-error', !!isError);
        } else {
          this.upMsg.hidden = true;
          this.upMsg.classList.remove('is-error');
        }
      }
    }

    // Write each magnet's print-ready link as a hidden line-item property
    // (_photo_N_url). Hidden props (underscore-prefixed) show in the order admin
    // but not to the customer. Re-asserted on every gate pass because Dawn wipes
    // the form on variant change.
    _writeUrlProps() {
      if (!this.form) return;
      for (let i = 0; i < this.slots.length; i++) {
        const slot = this.slots[i];
        const url = this._allUploaded && slot && slot.uploadedUrl ? slot.uploadedUrl : '';
        this._setProp('_photo_' + (i + 1) + '_url', url);
      }
      // A smaller pack leaves _photo_N_url fields above the new count in the
      // form (Dawn keeps the form node), and the order would carry them all.
      this.form.querySelectorAll('input[name^="properties[_photo_"]').forEach((el) => {
        const n = photoPropIndex(el.name);
        if (n !== null && n > this.slots.length) el.remove();
      });
      // Tag the shape so the print pipeline knows the layout (square/rect/round).
      this._setProp('_shape', this._allUploaded ? shapeClass(this._shape) : '');
      // Which 2 x 3 magnets were uploaded landscape (stored rotated), e.g. "1, 4" or "none".
      this._setProp('_landscape', this._allUploaded ? landscapeProp(this.slots, this._shape) : '', true);
      // Visible on the order, the packing slip and the customer's confirmation.
      // Set regardless of upload state: the size is known as soon as a variant
      // is chosen, and "Fridge Photo Magnets / 6-Pack" alone never says 2 x 3.
      this._setProp('Size', sizeLabel(this._print, this._shape));
      // Visible on the order so the press can check the text before pressing.
      this._setProp('Captions', captionSummary(this.slots), true);
      orderPropInputs(this.form);
    }

    _setProp(name, value, removeIfEmpty) {
      let el = this.form.querySelector('input[name="properties[' + name + ']"]');
      if (removeIfEmpty && !value) {
        if (el) el.remove();
        return;
      }
      if (!el) {
        el = document.createElement('input');
        el.type = 'hidden';
        el.name = 'properties[' + name + ']';
        this.form.appendChild(el);
      }
      el.value = value;
    }

    // Render the on-page thumbnail strip after a successful upload: first 6
    // photos, then a "+N more" button that reveals the rest.
    _renderThumbs() {
      if (!this.thumbsEl) return;
      const filled = this.slots.filter((s) => s.filled && s.thumb);
      if (!this._allUploaded || filled.length === 0) {
        this._clear(this.thumbsEl);
        this.thumbsEl.hidden = true;
        return;
      }
      const cls = shapeClass(this._shape);
      const list = this._sameForAll ? [filled[0]] : filled;
      const label = this._sameForAll
        ? fmt(this.t.thumbsOneForAll, { total: this.slots.length })
        : fmt(list.length === 1 ? this.t.thumbsOne : this.t.thumbsMany, { n: list.length });

      this._clear(this.thumbsEl);
      const lbl = document.createElement('span');
      lbl.className = 'fm-up__thumbslbl';
      lbl.textContent = '✓ ' + label;

      const row = document.createElement('div');
      row.className = 'fm-up__thumbrow';
      const LIMIT = 6;
      list.forEach((s, i) => {
        const img = document.createElement('img');
        img.className =
          'fm-up__thumb fm-up__thumb--' + cls +
          (cls === 'rect' && s.layout === 'landscape' ? ' fm-up__thumb--land' : '') +
          (i >= LIMIT ? ' fm-up__thumb--hidden' : '');
        img.src = s.thumb;
        img.alt = fmt(this.t.thumbAlt, { n: i + 1 });
        row.appendChild(img);
      });
      if (list.length > LIMIT) {
        const more = document.createElement('button');
        more.type = 'button';
        more.className = 'fm-up__more';
        more.textContent = fmt(this.t.more, { n: list.length - LIMIT });
        more.addEventListener('click', () => {
          row.querySelectorAll('.fm-up__thumb--hidden').forEach((t) => t.classList.remove('fm-up__thumb--hidden'));
          more.remove();
        });
        row.appendChild(more);
      }
      this.thumbsEl.appendChild(lbl);
      this.thumbsEl.appendChild(row);
      this.thumbsEl.hidden = false;
    }
  }
  window.customElements.define('photo-uploader', PhotoUploader);
}

// build: editor-v3-closegate
